import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag,
  FormControl, FormLabel, SimpleGrid,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { ImmOrden, listarOrdenes, eliminarOrden, vincularOrden } from '../../../api/immApi';
import { fmt, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge } from '../_shared';
import FormularioOrden from './FormularioOrden';

const expedienteLabel = (item: ImmOrden) => item.expediente?.folio ?? `#${item.expedienteId}`;

const VinculoBadges: React.FC<{ orden: ImmOrden }> = ({ orden }) => (
  <HStack spacing={1} flexWrap="wrap">
    {orden.causaId != null && <Badge colorScheme="purple" fontSize="0.65rem">Causa</Badge>}
    {orden.audienciaId != null && <Badge colorScheme="blue" fontSize="0.65rem">Audiencia</Badge>}
    {orden.detencionId != null && <Badge colorScheme="orange" fontSize="0.65rem">Detención</Badge>}
    {orden.causaId == null && orden.audienciaId == null && orden.detencionId == null && <Text fontSize="xs" color="gray.400">—</Text>}
  </HStack>
);

const TablaOrdenes: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const vincularD = useDisclosure();
  const bajaD = useDisclosure();

  const [data, setData] = useState<ImmOrden[]>([]);
  const [selected, setSelected] = useState<ImmOrden | null>(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Estado del modal de vinculación
  const [vinculando, setVinculando] = useState(false);
  const [causaId, setCausaId] = useState('');
  const [audienciaId, setAudienciaId] = useState('');
  const [detencionId, setDetencionId] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarOrdenes());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? expedienteLabel(item).toLowerCase().includes(q) ||
        item.numeroExpediente?.toLowerCase().includes(q) ||
        item.autoridadEmisora?.toLowerCase().includes(q)
      : true;
    return matchEstatus && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const abrirVincular = (item: ImmOrden) => {
    setSelected(item);
    setCausaId(item.causaId != null ? String(item.causaId) : '');
    setAudienciaId(item.audienciaId != null ? String(item.audienciaId) : '');
    setDetencionId(item.detencionId != null ? String(item.detencionId) : '');
    vincularD.onOpen();
  };

  const handleVincularConfirmar = async () => {
    if (!selected) return;
    setVinculando(true);
    try {
      const payload: { causaId?: number; audienciaId?: number; detencionId?: number } = {};
      if (causaId.trim() !== '') payload.causaId = Number(causaId);
      if (audienciaId.trim() !== '') payload.audienciaId = Number(audienciaId);
      if (detencionId.trim() !== '') payload.detencionId = Number(detencionId);
      await vincularOrden(selected.id, payload);
      toast({ title: 'Vínculos actualizados', status: 'success', duration: 3000 });
      vincularD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudieron guardar los vínculos.', status: 'error', duration: 4000 });
    } finally {
      setVinculando(false);
    }
  };

  const handleBajaConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await eliminarOrden(selected.id);
      toast({ title: 'Orden eliminada', status: 'success', duration: 3000 });
      bajaD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Expediente': expedienteLabel(item),
      'No. expediente': item.numeroExpediente || '',
      'Autoridad': item.autoridadEmisora || '',
      'Emisión': fmt(item.fechaEmision),
      'Vencimiento': fmt(item.fechaVencimiento),
      'Estatus': item.estatus,
      'Causa': item.causaId ?? '',
      'Audiencia': item.audienciaId ?? '',
      'Detención': item.detencionId ?? '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ordenes IMM');
    XLSX.writeFile(wb, `Ordenes_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por expediente, no. expediente o autoridad..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '300px' }} />
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="150px">
            <option value="Vigente">Vigente</option>
            <option value="Vencida">Vencida</option>
            <option value="Cancelada">Cancelada</option>
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nueva orden</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Expediente</Th>
              <Th color="white" fontSize="xs">No. expediente</Th>
              <Th color="white" fontSize="xs">Autoridad</Th>
              <Th color="white" fontSize="xs">Emisión</Th>
              <Th color="white" fontSize="xs">Vencimiento</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Vínculos</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={8} textAlign="center" py={8}><Text color="gray.400">No se encontraron órdenes</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{expedienteLabel(item)}</Tag></Td>
                  <Td>{item.numeroExpediente || '—'}</Td>
                  <Td><Text noOfLines={1} fontSize="sm">{item.autoridadEmisora || '—'}</Text></Td>
                  <Td>{fmt(item.fechaEmision)}</Td>
                  <Td>{fmt(item.fechaVencimiento)}</Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td><VinculoBadges orden={item} /></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button>
                      <Button size="xs" colorScheme="purple" variant="outline" onClick={() => abrirVincular(item)}>Vincular</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelected(item); bajaD.onOpen(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Orden de protección</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioOrden modo="view" data={selected} />}</ModalBody>
          <ModalFooter><Button onClick={verD.onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar orden</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioOrden modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nueva orden</ModalHeader>
          <ModalBody py={5}><FormularioOrden modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Vincular */}
      <Modal isOpen={vincularD.isOpen} onClose={vincularD.onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Vincular con Juzgado / Seguridad</ModalHeader>
          <ModalBody py={5}>
            <Text fontSize="sm" color="gray.500" mb={4}>
              Ingresa los identificadores de causa, audiencia y/o detención asociados a esta orden. Deja en blanco los que no apliquen.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Causa ID</FormLabel>
                <Input type="number" value={causaId} onChange={(e) => setCausaId(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Audiencia ID</FormLabel>
                <Input type="number" value={audienciaId} onChange={(e) => setAudienciaId(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Detención ID</FormLabel>
                <Input type="number" value={detencionId} onChange={(e) => setDetencionId(e.target.value)} />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={vincularD.onClose} isDisabled={vinculando}>Cancelar</Button>
            <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleVincularConfirmar} isLoading={vinculando} loadingText="Guardando...">
              Guardar vínculos
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Eliminar físico */}
      <AlertDialog isOpen={bajaD.isOpen} leastDestructiveRef={cancelRef} onClose={bajaD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Eliminar orden</AlertDialogHeader>
          <AlertDialogBody>
            La orden del expediente <strong>{selected ? expedienteLabel(selected) : ''}</strong> se eliminará de forma permanente. ¿Continuar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={bajaD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleBajaConfirmar} ml={3} isLoading={procesando} loadingText="Procesando...">Eliminar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaOrdenes;
